import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ProposalShareComercialPicker.css';

export type ShareComercialOption = {
  value: number;
  label: string;
  username: string;
};

type ProposalShareComercialPickerProps = {
  options: ShareComercialOption[];
  value: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
};

const MENU_ID = 'proposal-share-comercial-menu';

/**
 * Searchable multi-picker for co-comercials (no Ant Design Select — reliable list in proposal modal).
 */
const ProposalShareComercialPicker: React.FC<ProposalShareComercialPickerProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Pesquisar comercial...',
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const blurCloseTimerRef = useRef<number | null>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const selectedOptions = useMemo(
    () =>
      value
        .map((id) => options.find((o) => o.value === id))
        .filter((o): o is ShareComercialOption => Boolean(o)),
    [value, options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (selectedSet.has(o.value)) return false;
      if (!q) return true;
      return o.label.toLowerCase().includes(q);
    });
  }, [options, query, selectedSet]);

  const clearBlurCloseTimer = useCallback(() => {
    if (blurCloseTimerRef.current != null) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    clearBlurCloseTimer();
    setOpen(false);
  }, [clearBlurCloseTimer]);

  const isEventInsidePicker = useCallback((target: EventTarget | null) => {
    const t = target as Node | null;
    if (!t) return false;
    if (anchorRef.current?.contains(t)) return true;
    const menu = document.getElementById(MENU_ID);
    return Boolean(menu?.contains(t));
  }, []);

  const updateMenuPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const maxMenuH = 280;
    const openUp = spaceBelow < maxMenuH + 16 && rect.top > maxMenuH + 16;
    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 320),
      zIndex: 10050,
      maxHeight: maxMenuH,
      ...(openUp
        ? { bottom: viewportH - rect.top + 4, top: 'auto' }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPosition, query, value.length]);

  useEffect(() => {
    if (!open) return;
    // Capture phase: proposal modal stops mousedown propagation on .modal-content,
    // so a bubble-phase document listener never sees taps inside the modal.
    const onDocPointerDown = (e: PointerEvent) => {
      if (isEventInsidePicker(e.target)) return;
      closeMenu();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [open, closeMenu, isEventInsidePicker]);

  useEffect(() => () => clearBlurCloseTimer(), [clearBlurCloseTimer]);

  const addUser = (id: number) => {
    if (selectedSet.has(id)) return;
    onChange([...value, id]);
    setQuery('');
    // Keep list usable for multi-add; focus stays so more can be added.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeUser = (id: number) => {
    onChange(value.filter((x) => x !== id));
  };

  const menu =
    open &&
    createPortal(
      <div
        id={MENU_ID}
        className="proposal-share-comercial-menu"
        style={menuStyle}
        role="listbox"
        aria-label="Comerciais disponíveis"
      >
        {filtered.length === 0 ? (
          <div className="proposal-share-comercial-menu-empty">
            {options.length === 0
              ? 'Sem utilizadores disponíveis.'
              : selectedSet.size === options.length
                ? 'Todos os comerciais já foram adicionados.'
                : 'Nenhum resultado para a pesquisa.'}
          </div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              className="proposal-share-comercial-menu-item"
              role="option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addUser(o.value)}
            >
              {o.label}
            </button>
          ))
        )}
      </div>,
      document.body
    );

  return (
    <div className="proposal-share-comercial-picker" ref={anchorRef}>
      {selectedOptions.length > 0 && (
        <div className="proposal-share-comercial-chips">
          {selectedOptions.map((o) => (
            <span key={o.value} className="proposal-share-comercial-chip">
              <span className="proposal-share-comercial-chip-label">{o.username}</span>
              <button
                type="button"
                className="proposal-share-comercial-chip-remove"
                aria-label={`Remover ${o.username}`}
                onPointerDown={(e) => {
                  // Avoid input blur race before remove on touch devices.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeUser(o.value);
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        className="form-input proposal-share-comercial-search"
        value={query}
        placeholder={
          selectedOptions.length > 0 ? 'Adicionar outro comercial...' : placeholder
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          clearBlurCloseTimer();
          setOpen(true);
          updateMenuPosition();
        }}
        onBlur={() => {
          clearBlurCloseTimer();
          blurCloseTimerRef.current = window.setTimeout(() => {
            const active = document.activeElement;
            if (isEventInsidePicker(active)) return;
            closeMenu();
          }, 120);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            closeMenu();
            return;
          }
          if (e.key === 'Enter' && filtered.length > 0) {
            e.preventDefault();
            addUser(filtered[0].value);
          }
        }}
        autoComplete="off"
        aria-expanded={open}
        aria-controls={MENU_ID}
        aria-autocomplete="list"
      />
      {menu}
    </div>
  );
};

export default ProposalShareComercialPicker;
